import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

export const REPORT_BASE = '/quality/sample-investigations';
export const CATEGORIES = ['Fit', 'Function', 'Dimensions', 'Workmanship', 'Finish', 'Documentation', 'Packaging', 'Other'];
export const SEVERITIES = ['Critical', 'Major', 'Minor', 'Observation'];
export const RESULTS = ['Not assessed', 'Pass', 'Fail', 'Not applicable'];
export const DISPOSITIONS = ['Pending', 'Accepted', 'Conditional', 'Rejected', 'Retest required'];
export const RATING_DIMENSIONS = [['quality', 'Quality'], ['fitFunction', 'Fit and function'], ['finish', 'Finish'], ['documentation', 'Documentation']];
const CONTENT_KEYS = ['title', 'supplierId', 'parts', 'sample', 'summary', 'findings', 'conclusion', 'disposition', 'nextSteps', 'rating', 'followUpOf'];

export function newReport(params = new URLSearchParams()) {
  return { title: '', supplierId: params.get('supplierId') || '',
    parts: params.get('partNumber') ? [{ partNumber: params.get('partNumber'), revision: params.get('partRevision') || '', description: '', bomId: params.get('bomId') || '' }] : [],
    sample: { reference: '', lot: '', receivedQuantity: null, quantity: null, receivedAt: null, inspectedAt: null, reviewedBy: '', inspectionMethod: '', specificationReference: '' }, summary: '', findings: [],
    conclusion: '', disposition: 'Pending', nextSteps: '', rating: { quality: null, fitFunction: null, finish: null, documentation: null, comment: '' }, followUpOf: params.get('followUpOf') || '' };
}
export function newFinding() {
  return { id: window.crypto.randomUUID(), title: '', category: 'Fit', severity: 'Observation', result: 'Not assessed', expected: '', observed: '',
    requestedAction: '', owner: '', dueAt: null, supplierResponse: '', rootCause: '', correctiveAction: '', verification: '', evidence: [] };
}
export function reportContent(record) {
  const content = Object.fromEntries(CONTENT_KEYS.filter(key => record[key] !== undefined).map(key => [key, JSON.parse(JSON.stringify(record[key]))]));
  content.findings = (content.findings || []).map(finding => ({ ...finding, evidence: (finding.evidence || []).map(({ archiveId, caption }) => ({ archiveId, caption: caption || '' })) }));
  if (content.rating) {
    const rating = content.rating;
    content.rating = rating.rubricVersion ? { comment: rating.comment || '', rubricVersion: rating.rubricVersion,
      criteria: (rating.criteria || []).map(({ dimension, criterion, findingIds, rationale }) => ({ dimension, criterion, findingIds: findingIds || [], rationale: rationale || '' })),
      ...(rating.reassess === true ? { reassess: true } : {}) }
      : { ...Object.fromEntries(RATING_DIMENSIONS.map(([key]) => [key, rating[key] ?? null])), comment: rating.comment || '' };
  }
  return content;
}
export function scoreLabel(value) { return typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1)} / 5` : 'Not rated'; }
export function coverageLabel(rating = {}) {
  const dimensions = RATING_DIMENSIONS.filter(([key]) => Array.isArray(rating.assessedDimensions) ? rating.assessedDimensions.includes(key) : Number.isFinite(rating[key]));
  return `${dimensions.length} of ${RATING_DIMENSIONS.length} dimensions assessed (${Math.round(dimensions.length / RATING_DIMENSIONS.length * 100)}% coverage)${dimensions.length ? ` · ${dimensions.map(([, label]) => label).join(', ')}` : ''}`;
}
export function ratingMethodLabel(rating = {}) { return rating.rubricVersion ? `Rubric ${rating.rubricVersion}` : 'Manual assessment'; }
export function shortDate(value) { return value ? new Date(value).toLocaleDateString() : 'Not recorded'; }
export function reportHref(id, revision) { return `${REPORT_BASE}/${encodeURIComponent(id)}${revision ? `?revision=${encodeURIComponent(revision)}` : ''}`; }
export function useInvestigationApi() {
  const { getToken } = useAuth();
  return useCallback(async (path = '', options = {}) => {
    const token = await getToken();
    if (!token) throw new Error('Sign in again to continue.');
    const multipart = options.body instanceof FormData;
    const response = await window.fetch(`${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}${options.base || REPORT_BASE}${path}`, {
      method: options.method || 'GET', credentials: 'omit', redirect: 'error', signal: options.signal,
      headers: { Authorization: `Bearer ${token}`, ...(!multipart && options.body ? { 'Content-Type': 'application/json' } : {}) },
      ...(options.body ? { body: multipart ? options.body : JSON.stringify(options.body) } : {}),
    });
    let data;
    try { data = await response.json(); } catch { throw new Error('The server response could not be confirmed. Your input has been kept.'); }
    if (!response.ok || data.ok === false) {
      const failure = new Error(data.message || data.error || 'The report could not be loaded.');
      failure.status = response.status; failure.data = data; throw failure;
    }
    return data;
  }, [getToken]);
}
